import { useState, useEffect, useRef, useCallback } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Product } from "@/lib/api";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";
import { Loader2, Phone, Copy, CheckCircle, CreditCard, QrCode, ExternalLink, Ticket, Clock, AlertCircle, ChevronDown } from "lucide-react";
import { Badge } from "@/components/ui/badge";
import { AppliedVoucher } from "./ProductDetailDialog";
import { Progress } from "@/components/ui/progress";
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from "@/components/ui/collapsible";

interface CheckoutDialogProps {
  product: Product | null;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  appliedVoucher?: AppliedVoucher | null;
  discountedPrice?: number;
}

type CheckoutStep = "form" | "payment" | "success" | "expired";
type PaymentMethod = "transfer" | "qris";
type PaymentStatus = "pending" | "paid" | "delivered" | "cancelled" | "refunded";

interface OrderResult {
  orderId: string;
  orderNumber: string;
  totalAmount: number;
  whatsappMessage?: string;
  invoicePin: string;
  expiresAt?: string;
}

interface PaymentSettings {
  transferEnabled: boolean;
  qrisEnabled: boolean;
  qrisMode: "manual" | "dynamic" | "gateway";
  bankName: string;
  accountNumber: string;
  accountHolder: string;
  qrisImageUrl: string;
  qrisStaticCode: string;
  whatsappNumber: string;
}

const defaultPaymentSettings: PaymentSettings = {
  transferEnabled: true,
  qrisEnabled: false,
  qrisMode: "manual",
  bankName: "BCA",
  accountNumber: "1234567890",
  accountHolder: "UangCuan.ID",
  qrisImageUrl: "",
  qrisStaticCode: "",
  whatsappNumber: "6281234567890",
};

const POLLING_INTERVAL = 5000; // 5 seconds
const COUNTDOWN_INTERVAL = 1000; // 1 second

export function CheckoutDialog({ product, open, onOpenChange, appliedVoucher, discountedPrice }: CheckoutDialogProps) {
  const [step, setStep] = useState<CheckoutStep>("form");
  const [paymentMethod, setPaymentMethod] = useState<PaymentMethod>("transfer");
  const [whatsapp, setWhatsapp] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [orderResult, setOrderResult] = useState<OrderResult | null>(null);
  const [paymentSettings, setPaymentSettings] = useState<PaymentSettings>(defaultPaymentSettings);
  const [dynamicQrisDataUrl, setDynamicQrisDataUrl] = useState<string | null>(null);
  const [generatingQris, setGeneratingQris] = useState(false);
  const [timeRemaining, setTimeRemaining] = useState<number>(0);
  const [paymentReceived, setPaymentReceived] = useState(false);
  
  const pollingIntervalRef = useRef<NodeJS.Timeout | null>(null);
  const countdownIntervalRef = useRef<NodeJS.Timeout | null>(null);

  // Cleanup intervals on unmount
  useEffect(() => {
    return () => {
      if (pollingIntervalRef.current) clearInterval(pollingIntervalRef.current);
      if (countdownIntervalRef.current) clearInterval(countdownIntervalRef.current);
    };
  }, []);

  useEffect(() => {
    if (open) {
      fetchPaymentSettings();
    } else {
      // Cleanup when dialog closes
      if (pollingIntervalRef.current) clearInterval(pollingIntervalRef.current);
      if (countdownIntervalRef.current) clearInterval(countdownIntervalRef.current);
    }
  }, [open]);

  // Check payment status polling
  const checkPaymentStatus = useCallback(async () => {
    if (!orderResult?.orderNumber || !orderResult?.invoicePin) return;

    try {
      const { data, error } = await supabase.rpc("get_order_by_number_and_pin", {
        _order_number: orderResult.orderNumber,
        _invoice_pin: orderResult.invoicePin,
      });

      if (error) {
        console.error("Error checking payment status:", error);
        return;
      }

      const order = data?.[0];
      if (!order) return;

      const status = order.status as PaymentStatus;

      // Check if payment received (paid or delivered)
      if (status === "paid" || status === "delivered") {
        setPaymentReceived(true);
        // Stop polling
        if (pollingIntervalRef.current) clearInterval(pollingIntervalRef.current);
        if (countdownIntervalRef.current) clearInterval(countdownIntervalRef.current);

        // Show success notification
        toast.success("🎉 Pembayaran diterima! Terima kasih atas pesanan Anda.", {
          duration: 6000,
        });

        // Auto redirect to WhatsApp after 2 seconds
        setTimeout(() => {
          openWhatsAppConfirmation();
          setStep("success");
        }, 2000);
      }

      // Check if order cancelled/expired
      if (status === "cancelled") {
        setStep("expired");
        if (pollingIntervalRef.current) clearInterval(pollingIntervalRef.current);
        if (countdownIntervalRef.current) clearInterval(countdownIntervalRef.current);
      }
    } catch (err) {
      console.error("Error in payment status check:", err);
    }
  }, [orderResult?.orderNumber, orderResult?.invoicePin]);

  // Start polling when entering payment step
  useEffect(() => {
    if (step === "payment" && orderResult) {
      // Start polling for payment status
      pollingIntervalRef.current = setInterval(checkPaymentStatus, POLLING_INTERVAL);
      
      // Start countdown timer
      if (orderResult.expiresAt) {
        const updateCountdown = () => {
          const now = new Date().getTime();
          const expiry = new Date(orderResult.expiresAt!).getTime();
          const remaining = Math.max(0, Math.floor((expiry - now) / 1000));
          
          setTimeRemaining(remaining);
          
          if (remaining <= 0) {
            setStep("expired");
            if (pollingIntervalRef.current) clearInterval(pollingIntervalRef.current);
            if (countdownIntervalRef.current) clearInterval(countdownIntervalRef.current);
          }
        };
        
        updateCountdown(); // Initial update
        countdownIntervalRef.current = setInterval(updateCountdown, COUNTDOWN_INTERVAL);
      }
      
      return () => {
        if (pollingIntervalRef.current) clearInterval(pollingIntervalRef.current);
        if (countdownIntervalRef.current) clearInterval(countdownIntervalRef.current);
      };
    }
  }, [step, orderResult, checkPaymentStatus]);

  // Open WhatsApp confirmation
  const openWhatsAppConfirmation = () => {
    if (orderResult && product) {
      const defaultMessage = `Halo, saya sudah melakukan pembayaran untuk:\n\nOrder: ${orderResult.orderNumber}\nProduk: ${product.name}\nTotal: ${formatPrice(orderResult.totalAmount)}\n\nMohon diproses. Terima kasih!`;
      const message = orderResult.whatsappMessage 
        ? orderResult.whatsappMessage
            .replace("{order_number}", orderResult.orderNumber)
            .replace("{product_name}", product.name)
            .replace("{total}", formatPrice(orderResult.totalAmount))
        : defaultMessage;
      
      const adminWhatsapp = paymentSettings.whatsappNumber || "6281234567890";
      const whatsappUrl = `https://wa.me/${adminWhatsapp}?text=${encodeURIComponent(message)}`;
      window.open(whatsappUrl, "_blank");
    }
  };

  // Format time remaining
  const formatTimeRemaining = (seconds: number): string => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`;
  };

  const fetchPaymentSettings = async () => {
    try {
      const { data } = await supabase
        .from("site_settings")
        .select("value")
        .eq("key", "payment_settings")
        .maybeSingle();

      if (data?.value && typeof data.value === 'object' && !Array.isArray(data.value)) {
        const value = data.value as Record<string, unknown>;
        setPaymentSettings({
          transferEnabled: typeof value.transferEnabled === 'boolean' ? value.transferEnabled : defaultPaymentSettings.transferEnabled,
          qrisEnabled: typeof value.qrisEnabled === 'boolean' ? value.qrisEnabled : defaultPaymentSettings.qrisEnabled,
          qrisMode: value.qrisMode === 'gateway' ? 'gateway' : value.qrisMode === 'dynamic' ? 'dynamic' : 'manual',
          bankName: typeof value.bankName === 'string' ? value.bankName : defaultPaymentSettings.bankName,
          accountNumber: typeof value.accountNumber === 'string' ? value.accountNumber : defaultPaymentSettings.accountNumber,
          accountHolder: typeof value.accountHolder === 'string' ? value.accountHolder : defaultPaymentSettings.accountHolder,
          qrisImageUrl: typeof value.qrisImageUrl === 'string' ? value.qrisImageUrl : defaultPaymentSettings.qrisImageUrl,
          qrisStaticCode: typeof value.qrisStaticCode === 'string' ? value.qrisStaticCode : defaultPaymentSettings.qrisStaticCode,
          whatsappNumber: typeof value.whatsappNumber === 'string' ? value.whatsappNumber : defaultPaymentSettings.whatsappNumber,
        });
        
        // Set default payment method based on what's enabled
        if (value.transferEnabled) {
          setPaymentMethod("transfer");
        } else if (value.qrisEnabled) {
          setPaymentMethod("qris");
        }
      }
    } catch (error) {
      console.error("Error fetching payment settings:", error);
    }
  };

  if (!product) return null;

  const finalPrice = discountedPrice ?? product.price;
  const hasDiscount = appliedVoucher && finalPrice < product.price;
  const discountAmount = hasDiscount ? product.price - finalPrice : 0;

  const formatPrice = (price: number) => {
    return new Intl.NumberFormat("id-ID", {
      style: "currency",
      currency: "IDR",
      minimumFractionDigits: 0,
    }).format(price);
  };

  const validateWhatsapp = (number: string) => {
    const cleaned = number.replace(/\D/g, "");
    return cleaned.length >= 10 && cleaned.length <= 15;
  };

  // Generate a 6-digit PIN for invoice verification
  const generateInvoicePin = () => {
    return Math.floor(100000 + Math.random() * 900000).toString();
  };

  // Generate dynamic QRIS from static QRIS code
  const generateDynamicQris = async (amount: number, orderId: string): Promise<string | null> => {
    if (!paymentSettings.qrisStaticCode || paymentSettings.qrisMode !== 'dynamic') {
      return null;
    }

    setGeneratingQris(true);
    try {
      const { data, error } = await supabase.functions.invoke('generate-dynamic-qris', {
        body: {
          qrisCode: paymentSettings.qrisStaticCode,
          amount: amount,
          orderId: orderId,
        },
      });

      if (error) {
        console.error('Error generating dynamic QRIS:', error);
        toast.error('Gagal generate QRIS dinamis');
        return null;
      }

      if (data?.dynamicQris) {
        // Generate QR code data URL from the dynamic QRIS string
        // Using a simple canvas-based QR code generation
        const qrDataUrl = await generateQRCodeDataUrl(data.dynamicQris);
        return qrDataUrl;
      }

      return null;
    } catch (err) {
      console.error('Error calling dynamic QRIS function:', err);
      return null;
    } finally {
      setGeneratingQris(false);
    }
  };

  // Generate QR code data URL from string
  const generateQRCodeDataUrl = async (text: string): Promise<string> => {
    // Use a simple canvas-based QR generation via external library
    // For now, we'll use a public QR API as fallback
    const encodedText = encodeURIComponent(text);
    return `https://api.qrserver.com/v1/create-qr-code/?size=300x300&data=${encodedText}&ecc=H`;
  };

  const handleSubmitOrder = async () => {
    if (!validateWhatsapp(whatsapp)) {
      toast.error("Nomor WhatsApp tidak valid");
      return;
    }

    setIsLoading(true);
    try {
      const { data, error } = await supabase.functions.invoke("create-order-public", {
        body: {
          productId: product.id,
          customerWhatsapp: whatsapp,
          quantity: 1,
          voucherId: appliedVoucher?.id ?? null,
        },
      });

      if (error) {
        console.error("create-order-public invoke error:", error);
        toast.error("Gagal membuat order: " + error.message);
        return;
      }

      if (!data?.id || !data?.orderNumber || !data?.invoicePin) {
        console.error("create-order-public bad response:", data);
        toast.error("Gagal membuat order: respons tidak valid");
        return;
      }

      setOrderResult({
        orderId: data.id,
        orderNumber: data.orderNumber,
        totalAmount: data.totalAmount,
        whatsappMessage: data.whatsappMessage || undefined,
        invoicePin: data.invoicePin,
        expiresAt: data.expiresAt || undefined,
      });

      // Reset payment received state
      setPaymentReceived(false);

      // Generate dynamic QRIS if mode is dynamic and payment method is QRIS
      if (paymentMethod === 'qris' && paymentSettings.qrisMode === 'dynamic' && paymentSettings.qrisStaticCode) {
        const qrDataUrl = await generateDynamicQris(data.totalAmount, data.id);
        if (qrDataUrl) {
          setDynamicQrisDataUrl(qrDataUrl);
        }
      }

      setStep("payment");
      toast.success("Order berhasil dibuat!");
    } catch (error: any) {
      console.error("Error creating order:", error);
      toast.error("Gagal membuat order: " + error.message);
    } finally {
      setIsLoading(false);
    }
  };

  const handleCopyOrderNumber = () => {
    if (orderResult) {
      navigator.clipboard.writeText(orderResult.orderNumber);
      toast.success("Nomor order disalin!");
    }
  };

  const handleCopyAccountNumber = () => {
    navigator.clipboard.writeText(paymentSettings.accountNumber);
    toast.success("Nomor rekening disalin!");
  };

  const handleClose = () => {
    // Cleanup intervals
    if (pollingIntervalRef.current) clearInterval(pollingIntervalRef.current);
    if (countdownIntervalRef.current) clearInterval(countdownIntervalRef.current);
    
    setStep("form");
    setWhatsapp("");
    setPaymentMethod("transfer");
    setOrderResult(null);
    setDynamicQrisDataUrl(null);
    setTimeRemaining(0);
    setPaymentReceived(false);
    onOpenChange(false);
  };

  const handleConfirmPayment = () => {
    // Open WhatsApp with confirmation message
    if (orderResult) {
      const defaultMessage = `Halo, saya sudah melakukan pembayaran untuk:\n\nOrder: ${orderResult.orderNumber}\nProduk: ${product.name}\nTotal: ${formatPrice(orderResult.totalAmount)}\n\nMohon diproses. Terima kasih!`;
      const message = orderResult.whatsappMessage 
        ? orderResult.whatsappMessage
            .replace("{order_number}", orderResult.orderNumber)
            .replace("{product_name}", product.name)
            .replace("{total}", formatPrice(orderResult.totalAmount))
        : defaultMessage;
      
      const adminWhatsapp = paymentSettings.whatsappNumber || "6281234567890";
      const whatsappUrl = `https://wa.me/${adminWhatsapp}?text=${encodeURIComponent(message)}`;
      window.open(whatsappUrl, "_blank");
    }
    setStep("success");
  };

  return (
    <Dialog open={open} onOpenChange={handleClose}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle>
            {step === "form" && "Checkout"}
            {step === "payment" && (paymentMethod === "transfer" ? "Pembayaran Transfer Bank" : "Pembayaran QRIS")}
            {step === "success" && "Pesanan Berhasil"}
            {step === "expired" && "Order Kadaluarsa"}
          </DialogTitle>
        </DialogHeader>

        {step === "form" && (
          <div className="space-y-6">
            {/* Product Summary */}
            <div className="p-4 bg-muted/50 rounded-lg space-y-2">
              <h3 className="font-semibold text-foreground">{product.name}</h3>
              <div className="flex justify-between text-sm">
                <span className="text-muted-foreground">Durasi</span>
                <span className="text-foreground">{product.duration} {product.durationUnit}</span>
              </div>
              <div className="flex justify-between text-sm">
                <span className="text-muted-foreground">Harga</span>
                {hasDiscount ? (
                  <div className="text-right">
                    <span className="text-muted-foreground line-through text-xs mr-2">{formatPrice(product.price)}</span>
                    <span className="font-bold text-success">{formatPrice(finalPrice)}</span>
                  </div>
                ) : (
                  <span className="font-bold text-primary">{formatPrice(product.price)}</span>
                )}
              </div>
              {hasDiscount && appliedVoucher && (
                <div className="flex items-center justify-between text-sm pt-2 border-t border-border">
                  <div className="flex items-center gap-2">
                    <Ticket className="h-4 w-4 text-success" />
                    <span className="text-success font-medium">{appliedVoucher.code}</span>
                  </div>
                  <span className="text-success">-{formatPrice(discountAmount)}</span>
                </div>
              )}
            </div>

            {/* Payment Method Selection */}
            <div className="space-y-3">
              <Label>Metode Pembayaran</Label>
              <div className="grid grid-cols-2 gap-3">
                <button
                  type="button"
                  onClick={() => setPaymentMethod("transfer")}
                  disabled={!paymentSettings.transferEnabled}
                  className={`p-4 rounded-lg border-2 transition-all ${
                    !paymentSettings.transferEnabled 
                      ? "border-border bg-muted/50 cursor-not-allowed"
                      : paymentMethod === "transfer"
                        ? "border-primary bg-primary/10"
                        : "border-border hover:border-muted-foreground"
                  }`}
                >
                  <CreditCard className={`h-6 w-6 mx-auto mb-2 ${paymentSettings.transferEnabled ? "text-primary" : "text-muted-foreground"}`} />
                  <p className={`text-sm font-medium ${!paymentSettings.transferEnabled && "text-muted-foreground"}`}>Transfer Bank</p>
                </button>
                <button
                  type="button"
                  onClick={() => setPaymentMethod("qris")}
                  disabled={!paymentSettings.qrisEnabled}
                  className={`p-4 rounded-lg border-2 transition-all relative ${
                    !paymentSettings.qrisEnabled 
                      ? "border-border bg-muted/50 cursor-not-allowed"
                      : paymentMethod === "qris"
                        ? "border-primary bg-primary/10"
                        : "border-border hover:border-muted-foreground"
                  }`}
                >
                  {!paymentSettings.qrisEnabled && (
                    <Badge variant="secondary" className="absolute -top-2 -right-2 text-xs">
                      Tidak Aktif
                    </Badge>
                  )}
                  <QrCode className={`h-6 w-6 mx-auto mb-2 ${paymentSettings.qrisEnabled ? "text-primary" : "text-muted-foreground"}`} />
                  <p className={`text-sm font-medium ${!paymentSettings.qrisEnabled && "text-muted-foreground"}`}>QRIS</p>
                </button>
              </div>
            </div>

            {/* WhatsApp Input */}
            <div className="space-y-2">
              <Label htmlFor="whatsapp" className="flex items-center gap-2">
                <Phone className="h-4 w-4" />
                Nomor WhatsApp
              </Label>
              <Input
                id="whatsapp"
                type="tel"
                placeholder="08xxxxxxxxxx"
                value={whatsapp}
                onChange={(e) => setWhatsapp(e.target.value)}
                className="text-lg"
              />
              <p className="text-xs text-muted-foreground">
                Receipt dan detail akun akan dikirim ke nomor ini
              </p>
            </div>

            {/* Total */}
            <div className="p-4 bg-primary/10 rounded-lg">
              <div className="flex justify-between items-center">
                <span className="font-medium text-foreground">Total Pembayaran</span>
                <span className="text-xl font-bold text-primary">{formatPrice(finalPrice)}</span>
              </div>
              {hasDiscount && (
                <p className="text-xs text-success mt-1 text-right">
                  Hemat {formatPrice(discountAmount)}!
                </p>
              )}
            </div>

            {/* Submit Button */}
            <Button 
              className="w-full gradient-primary text-primary-foreground"
              onClick={handleSubmitOrder}
              disabled={isLoading || !whatsapp}
            >
              {isLoading ? (
                <>
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                  Memproses...
                </>
              ) : (
                "Lanjut ke Pembayaran"
              )}
            </Button>
          </div>
        )}

        {step === "payment" && orderResult && (
          <div className="space-y-6">
            {/* Payment Received Notification */}
            {paymentReceived && (
              <div className="p-4 bg-green-500/10 border border-green-500/30 rounded-lg">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-full bg-green-500/20 flex items-center justify-center">
                    <CheckCircle className="h-6 w-6 text-green-500 animate-pulse" />
                  </div>
                  <div>
                    <p className="font-semibold text-green-600">Pembayaran Diterima!</p>
                    <p className="text-sm text-muted-foreground">Mengarahkan ke WhatsApp...</p>
                  </div>
                </div>
              </div>
            )}

            {/* Countdown Timer */}
            {!paymentReceived && timeRemaining > 0 && (
              <div className={`p-3 rounded-lg border ${timeRemaining <= 300 ? 'bg-destructive/10 border-destructive/30' : 'bg-amber-500/10 border-amber-500/30'}`}>
                <div className="flex items-center justify-between mb-2">
                  <div className="flex items-center gap-2">
                    <Clock className={`h-4 w-4 ${timeRemaining <= 300 ? 'text-destructive' : 'text-amber-600'}`} />
                    <span className={`text-sm font-medium ${timeRemaining <= 300 ? 'text-destructive' : 'text-amber-600'}`}>
                      Selesaikan pembayaran dalam
                    </span>
                  </div>
                  <span className={`font-mono font-bold text-lg ${timeRemaining <= 300 ? 'text-destructive' : 'text-amber-600'}`}>
                    {formatTimeRemaining(timeRemaining)}
                  </span>
                </div>
                <Progress 
                  value={(timeRemaining / 1800) * 100} 
                  className={`h-2 ${timeRemaining <= 300 ? '[&>div]:bg-destructive' : '[&>div]:bg-amber-500'}`}
                />
              </div>
            )}

            {/* Order Info */}
            <div className="p-4 bg-muted/50 rounded-lg space-y-3">
              <div className="flex justify-between items-center">
                <span className="text-sm text-muted-foreground">Order ID</span>
                <div className="flex items-center gap-2">
                  <code className="font-mono font-bold text-foreground">{orderResult.orderNumber}</code>
                  <Button 
                    variant="ghost" 
                    size="icon" 
                    className="h-8 w-8"
                    onClick={handleCopyOrderNumber}
                  >
                    <Copy className="h-4 w-4" />
                  </Button>
                </div>
              </div>
              <div className="flex justify-between">
                <span className="text-sm text-muted-foreground">Total</span>
                <span className="font-bold text-primary">{formatPrice(orderResult.totalAmount)}</span>
              </div>
              
              {/* Polling Status Indicator */}
              {!paymentReceived && (
                <div className="flex items-center gap-2 pt-2 border-t border-border">
                  <div className="w-2 h-2 rounded-full bg-primary animate-pulse" />
                  <span className="text-xs text-muted-foreground">Menunggu konfirmasi pembayaran...</span>
                </div>
              )}
            </div>

            {/* Bank Transfer Info */}
            {paymentMethod === "transfer" && (
              <div className="p-4 bg-primary/5 rounded-lg border border-primary/20 space-y-3">
                <h4 className="font-semibold text-foreground flex items-center gap-2">
                  <CreditCard className="h-5 w-5 text-primary" />
                  Transfer ke Rekening
                </h4>
                <div className="space-y-2">
                  <div className="flex justify-between items-center">
                    <span className="text-sm text-muted-foreground">Bank</span>
                    <span className="font-medium">{paymentSettings.bankName}</span>
                  </div>
                  <div className="flex justify-between items-center">
                    <span className="text-sm text-muted-foreground">No. Rekening</span>
                    <div className="flex items-center gap-2">
                      <code className="font-mono font-bold text-lg">{paymentSettings.accountNumber}</code>
                      <Button 
                        variant="ghost" 
                        size="icon" 
                        className="h-8 w-8"
                        onClick={handleCopyAccountNumber}
                      >
                        <Copy className="h-4 w-4" />
                      </Button>
                    </div>
                  </div>
                  <div className="flex justify-between items-center">
                    <span className="text-sm text-muted-foreground">Atas Nama</span>
                    <span className="font-medium">{paymentSettings.accountHolder}</span>
                  </div>
                </div>
              </div>
            )}

            {/* QRIS Info - Dynamic or Manual */}
            {paymentMethod === "qris" && (
              <div className="p-4 bg-primary/5 rounded-lg border border-primary/20 space-y-3">
                <h4 className="font-semibold text-foreground flex items-center gap-2">
                  <QrCode className="h-5 w-5 text-primary" />
                  Scan QRIS
                  {paymentSettings.qrisMode === 'dynamic' && (
                    <Badge variant="outline" className="text-xs text-green-600 border-green-600">
                      Nominal Otomatis
                    </Badge>
                  )}
                </h4>
                
                {generatingQris ? (
                  <div className="flex flex-col items-center justify-center py-8">
                    <Loader2 className="h-8 w-8 animate-spin text-primary mb-2" />
                    <p className="text-sm text-muted-foreground">Generating QRIS...</p>
                  </div>
                ) : (
                  <div className="flex justify-center">
                    {/* Show dynamic QRIS if available, otherwise show static */}
                    {dynamicQrisDataUrl ? (
                      <img 
                        src={dynamicQrisDataUrl} 
                        alt="Dynamic QRIS Code" 
                        className="max-w-[200px] rounded-lg border border-border"
                      />
                    ) : paymentSettings.qrisImageUrl ? (
                      <img 
                        src={paymentSettings.qrisImageUrl} 
                        alt="QRIS Code" 
                        className="max-w-[200px] rounded-lg border border-border"
                      />
                    ) : (
                      <div className="flex flex-col items-center py-8 text-muted-foreground">
                        <QrCode className="h-12 w-12 mb-2" />
                        <p className="text-sm">QRIS tidak tersedia</p>
                      </div>
                    )}
                  </div>
                )}
                
                <p className="text-center text-sm text-muted-foreground">
                  {paymentSettings.qrisMode === 'dynamic' && dynamicQrisDataUrl
                    ? `Scan untuk membayar ${formatPrice(orderResult?.totalAmount || finalPrice)} (nominal sudah terisi otomatis)`
                    : 'Scan kode QRIS di atas untuk melakukan pembayaran'
                  }
                </p>
              </div>
            )}

            {/* Payment Instructions - Collapsible */}
            {!paymentReceived && (
              <Collapsible>
                <CollapsibleTrigger asChild>
                  <Button variant="ghost" className="w-full flex items-center justify-between p-2 h-auto text-sm">
                    <span className="font-medium text-foreground">Cara Pembayaran</span>
                    <ChevronDown className="h-4 w-4 text-muted-foreground transition-transform duration-200 [&[data-state=open]>svg]:rotate-180" />
                  </Button>
                </CollapsibleTrigger>
                <CollapsibleContent className="text-sm text-muted-foreground space-y-1 px-2 pb-2">
                  {paymentMethod === "transfer" ? (
                    <>
                      <p>1. Transfer sesuai jumlah ke rekening di atas</p>
                      <p>2. Simpan bukti transfer</p>
                      <p>3. Pembayaran akan dikonfirmasi otomatis</p>
                      <p>4. Atau klik tombol di bawah untuk konfirmasi manual via WhatsApp</p>
                    </>
                  ) : (
                    <>
                      <p>1. Scan kode QRIS di atas dengan aplikasi e-wallet atau m-banking</p>
                      <p>2. Pastikan nominal sesuai dengan total pembayaran</p>
                      <p>3. Pembayaran akan dikonfirmasi otomatis</p>
                      <p>4. Atau klik tombol di bawah untuk konfirmasi manual via WhatsApp</p>
                    </>
                  )}
                </CollapsibleContent>
              </Collapsible>
            )}

            {/* Confirm Payment Button */}
            {!paymentReceived && (
              <Button 
                className="w-full gradient-primary text-primary-foreground"
                onClick={handleConfirmPayment}
              >
                <ExternalLink className="h-4 w-4 mr-2" />
                Konfirmasi via WhatsApp
              </Button>
            )}
          </div>
        )}

        {step === "success" && orderResult && (
          <div className="space-y-6 text-center py-4">
            <div className="flex justify-center">
              <div className="w-20 h-20 rounded-full bg-green-500/20 flex items-center justify-center">
                <CheckCircle className="h-10 w-10 text-green-500" />
              </div>
            </div>

            <div className="space-y-2">
              <h3 className="text-lg font-semibold text-foreground">Terima Kasih!</h3>
              <p className="text-sm text-muted-foreground">
                Pesanan Anda sedang diproses. Detail akun akan dikirim ke WhatsApp Anda setelah pembayaran dikonfirmasi.
              </p>
            </div>

            <div className="p-4 bg-muted/50 rounded-lg space-y-3">
              <div>
                <p className="text-sm text-muted-foreground">Order ID</p>
                <div className="flex items-center justify-center gap-2 mt-1">
                  <code className="text-lg font-mono font-bold text-foreground">{orderResult.orderNumber}</code>
                  <Button 
                    variant="ghost" 
                    size="icon" 
                    className="h-8 w-8"
                    onClick={handleCopyOrderNumber}
                  >
                    <Copy className="h-4 w-4" />
                  </Button>
                </div>
              </div>
              <div className="pt-2 border-t border-border">
                <p className="text-sm text-muted-foreground">PIN Verifikasi</p>
                <div className="flex items-center justify-center gap-2 mt-1">
                  <code className="text-xl font-mono font-bold text-primary tracking-widest">{orderResult.invoicePin}</code>
                  <Button 
                    variant="ghost" 
                    size="icon" 
                    className="h-8 w-8"
                    onClick={() => {
                      navigator.clipboard.writeText(orderResult.invoicePin);
                      toast.success("PIN disalin!");
                    }}
                  >
                    <Copy className="h-4 w-4" />
                  </Button>
                </div>
              </div>
              <p className="text-xs text-muted-foreground mt-2 text-center">
                ⚠️ Simpan Order ID dan PIN untuk melacak pesanan Anda
              </p>
            </div>

            <Button 
              className="w-full"
              variant="outline"
              onClick={handleClose}
            >
              Tutup
            </Button>
          </div>
        )}

        {/* Expired Step */}
        {step === "expired" && (
          <div className="space-y-6 text-center py-4">
            <div className="flex justify-center">
              <div className="w-20 h-20 rounded-full bg-destructive/20 flex items-center justify-center">
                <AlertCircle className="h-10 w-10 text-destructive" />
              </div>
            </div>

            <div className="space-y-2">
              <h3 className="text-lg font-semibold text-foreground">Order Kadaluarsa</h3>
              <p className="text-sm text-muted-foreground">
                Waktu pembayaran telah habis. Silakan buat order baru jika Anda masih ingin melakukan pembelian.
              </p>
            </div>

            {orderResult && (
              <div className="p-4 bg-muted/50 rounded-lg">
                <p className="text-sm text-muted-foreground">Order ID</p>
                <code className="text-lg font-mono font-bold text-foreground">{orderResult.orderNumber}</code>
              </div>
            )}

            <div className="flex gap-3">
              <Button 
                className="flex-1"
                variant="outline"
                onClick={handleClose}
              >
                Tutup
              </Button>
              <Button 
                className="flex-1 gradient-primary text-primary-foreground"
                onClick={() => {
                  setStep("form");
                  setOrderResult(null);
                  setDynamicQrisDataUrl(null);
                  setTimeRemaining(0);
                  setPaymentReceived(false);
                }}
              >
                Buat Order Baru
              </Button>
            </div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
